// RAG chat (PLAN §8, §7.1; task 28): the shapes the API, the AI package and the web page share, and the pure rules
// that turn what an answer cites into its caveats. The chat is read-only: it searches the Library and the graph
// through tools, and every statement it makes cites passages, claims or entities by a short ref (`P1`, `C2`, `E3`)
// that the server maps back to the real records.

import type { AttributionLevel, ClaimKind, ReviewStatus } from './claims';
import { type CompareRoute, ROUTE_LABELS } from './compare';
import type { CollectionDefaults } from './library';
import type { EntityKind } from './schema/types';
import { searchFold } from './search';
import type { DetectedKind, Sensitivity } from './uploads';

// ── Scope ────────────────────────────────────────────────────────────────────────────────────────────────────

/** What a conversation answers from: everything, one collection (and the ones nested in it), or one Case. */
export const CHAT_SCOPE_KINDS = ['all', 'collection', 'case'] as const;
export type ChatScopeKind = (typeof CHAT_SCOPE_KINDS)[number];
export const isChatScopeKind = (v: unknown): v is ChatScopeKind => CHAT_SCOPE_KINDS.includes(v as ChatScopeKind);

export interface ChatScope {
  kind: ChatScopeKind;
  /** Collection id or Case entity id; null for `all`. */
  id: string | null;
}
export const ALL_SCOPE: ChatScope = { kind: 'all', id: null };

/** A scope from untrusted input (a request body, a URL); null when it doesn't hold together. */
export function parseChatScope(v: unknown): ChatScope | null {
  if (v === undefined || v === null) return ALL_SCOPE;
  if (typeof v !== 'object' || Array.isArray(v)) return null;
  const o = v as Record<string, unknown>;
  const kind = o.kind ?? 'all';
  if (!isChatScopeKind(kind)) return null;
  if (kind === 'all') return ALL_SCOPE;
  return typeof o.id === 'string' && /^[A-Za-z0-9_-]{1,64}$/.test(o.id) ? { kind, id: o.id } : null;
}

// ── Limits ───────────────────────────────────────────────────────────────────────────────────────────────────

export const CHAT_QUESTION_MAX = 2000;
export const CHAT_TITLE_MAX = 120;
/** Tool rounds the model may ask for before it must answer. */
export const CHAT_MAX_TOOL_ROUNDS = 3;
/** Earlier turns sent with a question (the adopted answers, not the discarded Compare ones). */
export const CHAT_HISTORY_TURNS = 6;

/** A title for a new conversation, from its first question. */
export function chatTitle(question: string): string {
  const one = question.replace(/\s+/g, ' ').trim();
  return one.length > 80 ? `${one.slice(0, 79).trimEnd()}…` : one || 'New conversation';
}

// ── Citations ────────────────────────────────────────────────────────────────────────────────────────────────

/** Refs the model cites: `P` passage, `C` claim, `E` entity, then a number. */
export const CITE_REF = /^[PCE][1-9]\d{0,3}$/;

export interface PassageCitation {
  ref: string;
  kind: 'passage';
  /** The cited item was erased by an admin (task 44): only this placeholder is left. */
  erased?: boolean;
  chunkId: string;
  documentId: string;
  filename: string;
  title: string | null;
  page?: number;
  /** Transcripts: where the passage starts in the recording (ms), and who speaks. */
  timeMs?: number;
  speaker?: string;
  heading?: string;
  /** The passage (trimmed) as the model read it. */
  excerpt: string;
  lang: string | null;
  sensitivity: Sensitivity;
  /** Collections the document is in. */
  collections: { id: string; name: string }[];
  /** From interviews or another collection whose material is unverified (see `isUnverifiedMaterial`). */
  unverified: boolean;
  /** The reading guide of that collection, when it has one. */
  guide: { documentId: string; filename: string; collection: string } | null;
}

export interface ClaimCitation {
  ref: string;
  kind: 'claim';
  /** The cited item was erased by an admin (task 44): only this placeholder is left. */
  erased?: boolean;
  claimId: string;
  entityId: string;
  entityName: string;
  /** The claim in words ("Field: value"). */
  text: string;
  status: ReviewStatus;
  claimKind: ClaimKind;
  attributionLevel: AttributionLevel;
  sensitivity: Sensitivity;
}

export interface EntityCitation {
  ref: string;
  kind: 'entity';
  /** The cited item was erased by an admin (task 44): only this placeholder is left. */
  erased?: boolean;
  entityId: string;
  name: string;
  code: string;
  entityKind: EntityKind;
  status: ReviewStatus;
  sensitivity: Sensitivity;
}

export type ChatCitation = PassageCitation | ClaimCitation | EntityCitation;

/** One or more sentences of an answer and the refs that support them (empty: an unsupported statement). */
export interface AnswerSegment {
  text: string;
  cites: string[];
}

// ── Answers, messages, sessions ──────────────────────────────────────────────────────────────────────────────

export const CAVEAT_KINDS = [
  'unverified',
  'allegation',
  'unreviewed',
  'disputed',
  'off_record',
  'unsupported',
  'confidential',
  'fallback',
  'nothing_found',
] as const;
export type CaveatKind = (typeof CAVEAT_KINDS)[number];

export interface ChatCaveat {
  kind: CaveatKind;
  text: string;
  /** Reading guides to open (unverified collections). */
  guides?: { documentId: string; filename: string; collection: string }[];
}

/** A tool the model used while answering ("Searched the Library for …: 6 passages"). */
export interface ChatStep {
  tool: string;
  summary: string;
  found: number;
}

export interface ChatAnswer {
  route: CompareRoute;
  label: string;
  model: string | null;
  ok: boolean;
  /** Plain-language reason when the route failed. */
  error: string | null;
  isFallback: boolean;
  segments: AnswerSegment[];
  /** Only the items the answer cites, in first-cited order. */
  citations: ChatCitation[];
  caveats: ChatCaveat[];
  /** Highest sensitivity of what the answer's route was given (confidential → answered natively). */
  sensitivity: Sensitivity;
  steps: ChatStep[];
  costUsd: number;
}

/** `choose`: a Compare-mode answer waiting for the person to pick the better one. */
export const CHAT_MESSAGE_STATUSES = ['answered', 'choose', 'failed'] as const;
export type ChatMessageStatus = (typeof CHAT_MESSAGE_STATUSES)[number];

export interface ChatMessageView {
  id: string;
  role: 'user' | 'assistant';
  /** The question, or the answer in plain text (the adopted one in Compare mode). */
  content: string;
  createdAt: number;
  status: ChatMessageStatus;
  /** The answer shown in the conversation (the adopted one in Compare mode; null while choosing). */
  answer: ChatAnswer | null;
  /** Compare mode: every route's answer, side by side. */
  answers: ChatAnswer[] | null;
  adoptedRoute: CompareRoute | null;
}

export interface ChatSessionView {
  id: string;
  title: string;
  scope: ChatScope & { label: string };
  createdAt: number;
  updatedAt: number;
}

/** Server-sent events of one question (POST /api/chat/sessions/:id/messages). */
export type ChatEvent =
  | { type: 'question'; message: ChatMessageView }
  | { type: 'status'; text: string }
  | { type: 'routes'; routes: CompareRoute[]; compare: boolean; sensitivity: Sensitivity }
  | { type: 'step'; route: CompareRoute | null; step: ChatStep }
  | { type: 'answer'; answer: ChatAnswer }
  | { type: 'done'; message: ChatMessageView; session: ChatSessionView }
  | { type: 'error'; message: string };

// ── Rules ────────────────────────────────────────────────────────────────────────────────────────────────────

/**
 * Material to read as unverified (PLAN §8: "according to an interviewee, unverified"): documents in a collection
 * whose defaults treat its content as allegations or as doubtful (credibility 5–6, e.g. interviews), and raw
 * interview material (transcripts, interview syntheses) wherever it sits.
 */
export function isUnverifiedMaterial(defaults: Pick<CollectionDefaults, 'claimKind' | 'credibility'>, kind?: DetectedKind | null): boolean {
  if (defaults.claimKind === 'allegation') return true;
  if (defaults.credibility !== null && defaults.credibility >= 5) return true;
  return kind === 'transcript' || kind === 'interview_synthesis';
}

/** Question words that say nothing about the subject (English and French; other languages pass through). */
const STOPWORDS = new Set(
  (
    'the a an and or of to in on at by for from with about into over after before since until during between is are was were be been being ' +
    'do does did have has had who whom whose what which when where why how whether this that these those there their them they he she it its ' +
    'his her him we you your our my me i can could should would will shall may might must any some all each every more most other than then ' +
    'also just only not no yes please tell show give find list say says said know according did many much ' +
    'le la les un une des du de et ou au aux en dans sur par pour avec sans qui que quoi quel quelle quels quelles quand comment pourquoi ' +
    'est sont etait ete etre avoir ont il elle ils elles nous vous ce cette ces son sa ses leur leurs selon'
  ).split(' '),
);

/** The words of a question worth searching for: folded, 3+ letters, not stopwords, at most 8 (the search's limit). */
export function questionKeywords(question: string): string[] {
  return [...new Set(searchFold(question).split(' '))].filter(w => [...w].length >= 3 && !STOPWORDS.has(w)).slice(0, 8);
}

/**
 * Names a question may mention, for the first graph lookup: runs of capitalised words ("Meridian Policy Forum"),
 * words in scripts without case (Arabic), and ID codes (`MED_LANTERN`). At most four, longest first.
 */
export function questionNames(question: string): string[] {
  const out: string[] = [];
  const words = question.split(/[\s,;:!?()"“”«»]+/).filter(Boolean);
  let run: string[] = [];
  const flush = () => {
    const name = run.join(' ').replace(/[.'’]+$/, '');
    if (name && !STOPWORDS.has(searchFold(name))) out.push(name);
    run = [];
  };
  for (const w of words) {
    const clean = w.replace(/^[.'’]+|[.'’]+$/g, '');
    const cased = /^\p{Lu}/u.test(clean) || /^[A-Z][A-Z0-9_]{2,}$/.test(clean);
    const uncased = /^\p{L}/u.test(clean) && clean.toLowerCase() === clean.toUpperCase();
    // A capitalised question word ("Is", "Who") doesn't start a name.
    if (clean && (cased || uncased) && (run.length || !STOPWORDS.has(searchFold(clean)))) run.push(clean);
    else flush();
  }
  flush();
  return [...new Set(out)].sort((a, b) => b.length - a.length).slice(0, 4);
}

/** Refs cited anywhere in the answer, first-cited first. */
export function citedRefs(segments: readonly AnswerSegment[]): string[] {
  const out: string[] = [];
  for (const s of segments) for (const r of s.cites) if (!out.includes(r)) out.push(r);
  return out;
}

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/**
 * What a reader must know about an answer (PLAN §8): it rests on unverified collections or allegations, on
 * unreviewed or disputed data, on off-record material; some statements have no source; it was written natively
 * because confidential material was involved; or by a fallback route. Computed from what the answer cites, so the
 * caveats never depend on the model mentioning them.
 */
export function answerCaveats(a: Pick<ChatAnswer, 'segments' | 'citations' | 'sensitivity' | 'isFallback' | 'route'>): ChatCaveat[] {
  const out: ChatCaveat[] = [];
  const passages = a.citations.filter((c): c is PassageCitation => c.kind === 'passage');
  const claims = a.citations.filter((c): c is ClaimCitation => c.kind === 'claim');
  const entities = a.citations.filter((c): c is EntityCitation => c.kind === 'entity');

  const unverified = passages.filter(p => p.unverified);
  if (unverified.length) {
    const guides = new Map<string, NonNullable<PassageCitation['guide']>>();
    for (const p of unverified) if (p.guide) guides.set(p.guide.documentId, p.guide);
    const names = [...new Set(unverified.flatMap(p => p.collections.map(c => c.name)))];
    out.push({
      kind: 'unverified',
      text: `Rests partly on interviews or other unverified material${names.length ? ` (${names.join(', ')})` : ''}: what it reports is according to the source, not verified.${guides.size ? ' Read it as its reading guide explains.' : ''}`,
      ...(guides.size ? { guides: [...guides.values()] } : {}),
    });
  }
  const allegations = claims.filter(c => c.claimKind === 'allegation');
  if (allegations.length)
    out.push({ kind: 'allegation', text: `Cites ${plural(allegations.length, 'allegation')}: what a source says happened, not verified.` });
  const unreviewed = claims.filter(c => c.status === 'unreviewed').length + entities.filter(e => e.status === 'unreviewed').length;
  if (unreviewed)
    out.push({ kind: 'unreviewed', text: `Includes unreviewed data: ${plural(unreviewed, 'cited item')} not yet checked by a reviewer.` });
  const disputed = claims.filter(c => c.status === 'disputed').length + entities.filter(e => e.status === 'disputed').length;
  if (disputed) out.push({ kind: 'disputed', text: `Includes disputed data: sources disagree on ${plural(disputed, 'cited item')}.` });
  const offRecord = claims.filter(c => c.attributionLevel === 'off_record').length;
  if (offRecord)
    out.push({
      kind: 'off_record',
      text: `Draws on ${plural(offRecord, 'off-record item')}: read it here, but don't quote it outside Hoopoe.`,
    });
  const unsupported = a.segments.filter(s => s.text.trim() && !s.cites.length).length;
  if (unsupported)
    out.push({
      kind: 'unsupported',
      text: `${plural(unsupported, 'statement')} ${unsupported === 1 ? 'has' : 'have'} no source and ${unsupported === 1 ? 'is' : 'are'} marked. Don't rely on ${unsupported === 1 ? 'it' : 'them'}.`,
    });
  if (a.sensitivity === 'confidential')
    out.push({
      kind: 'confidential',
      text: 'Confidential material was involved, so this answer was written by a Cloudflare-native model and nothing left Cloudflare.',
    });
  if (a.isFallback)
    out.push({ kind: 'fallback', text: `Answered by ${ROUTE_LABELS[a.route]} because the usual AI route was unavailable.` });
  if (!a.citations.length && a.segments.length)
    out.push({ kind: 'nothing_found', text: 'Nothing in the Library or the graph supports this answer.' });
  return out;
}

/** The answer as plain text (for history and the message's `content`). */
export const answerText = (segments: readonly AnswerSegment[]) =>
  segments
    .map(s => s.text.trim())
    .filter(Boolean)
    .join(' ');

/** Where a passage citation opens: the Library document, at the recording time for transcripts. */
export function passageHref(p: Pick<PassageCitation, 'documentId' | 'timeMs'>): string {
  const t = p.timeMs !== undefined ? `?t=${Math.floor(p.timeMs / 1000)}` : '';
  return `/library/${encodeURIComponent(p.documentId)}${t}`;
}

/** "p. 3", "12:07 · Speaker B", or the heading: where in the document a passage is. */
export function passagePlace(p: Pick<PassageCitation, 'page' | 'timeMs' | 'speaker' | 'heading'>): string | null {
  if (p.timeMs !== undefined) {
    const s = Math.floor(p.timeMs / 1000);
    const hh = Math.floor(s / 3600);
    const mm = Math.floor((s % 3600) / 60);
    const ss = String(s % 60).padStart(2, '0');
    const time = hh ? `${hh}:${String(mm).padStart(2, '0')}:${ss}` : `${mm}:${ss}`;
    return p.speaker ? `${time} · ${p.speaker}` : time;
  }
  if (p.page) return `p. ${p.page}`;
  return p.heading ?? null;
}
